import type { CompanyCard } from "../../types/company";
import type { IntegrationAccount, IntegrationAccountDto, IntegrationInfo } from "../../types/crm-links";

type EnrichedIntegrationInfo = IntegrationInfo & {
    readonly accounts: readonly IntegrationAccount[];
};

export const createCrmLinksService = (deps: CompanyCard | undefined) => {
    const { salesforceInfo, hubspotInfo, microsoftDynamicsInfo } = deps || {};
    const integrations: EnrichedIntegrationInfo[] = [];

    const createHubSpotAccount = (dto: IntegrationAccountDto, hubspotId: number): IntegrationAccount => ({
        ...dto,
        link: `https://app.hubspot.com/contacts/${hubspotId}/company/${dto.crmAccountId}`,
    });

    const createSalesforceAccount = (dto: IntegrationAccountDto, companyDomain: string): IntegrationAccount => ({
        ...dto,
        link: `https://${companyDomain}/lightning/r/Account/${dto.crmAccountId}/view`,
    });

    const createMicrosoftDynamicsAccount = (dto: IntegrationAccountDto, instanceUrl: string): IntegrationAccount => ({
        ...dto,
        link: `https://${instanceUrl}/main.aspx?etn=account&id=${dto.crmAccountId}&pagetype=entityrecord`,
    });

    if (salesforceInfo?.accounts) {
        const enrichedSfAccounts = salesforceInfo.accounts.map((dto) =>
            createSalesforceAccount(dto, salesforceInfo.companyDomain),
        );
        integrations.push({
            ...salesforceInfo,
            accounts: enrichedSfAccounts,
        });
    }
    if (hubspotInfo?.accounts) {
        const enrichedHubspotAccounts = hubspotInfo.accounts.map((dto) =>
            createHubSpotAccount(dto, hubspotInfo.hubspotId),
        );
        integrations.push({
            ...hubspotInfo,
            accounts: enrichedHubspotAccounts,
        });
    }
    if (microsoftDynamicsInfo?.accounts && microsoftDynamicsInfo.instanceUrl) {
        const { instanceUrl } = microsoftDynamicsInfo;
        const enrichedDynamicsAccounts = microsoftDynamicsInfo.accounts.map((dto) =>
            createMicrosoftDynamicsAccount(dto, instanceUrl),
        );
        integrations.push({
            ...microsoftDynamicsInfo,
            accounts: enrichedDynamicsAccounts,
        });
    }

    const hasIntegration = () => integrations.some((i) => i.isIntegrated);

    const getAllAccounts = (): readonly IntegrationAccount[] =>
        integrations.filter((i) => i.isIntegrated).flatMap((i) => i.accounts);

    const getPreferredAccount = (): Readonly<IntegrationAccount> | undefined => getAllAccounts()[0];

    return {
        hasIntegration,
        getAllAccounts,
        getPreferredAccount,
    };
};
